'use client'

export const dynamic = 'force-dynamic'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useParams, useSearchParams } from 'next/navigation'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  FileText,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
} from 'lucide-react'
import MediaUpload from '../../../../components/MediaUpload'
import type { MediaUploadAsset } from '../../../../components/MediaUpload'
import {
  AIAlert,
  AIRecommendation,
  Badge,
  Button,
  ButtonLink,
  Card,
  StatusBadge,
} from '../../../../components/ui'
import type { MasjidReportIssueType } from '../../../../lib/masjid-reports-store'

const issueOptions: Array<{ value: MasjidReportIssueType; label: string; helper: string }> = [
  { value: 'wrong_location', label: 'Wrong location', helper: 'Address, map pin, area, or city is incorrect.' },
  { value: 'wrong_prayer_timing', label: 'Wrong prayer timing', helper: 'Daily salah, iqamah, or Jumuah timing needs correction.' },
  { value: 'wrong_contact', label: 'Wrong contact', helper: 'Phone, email, or committee contact is outdated.' },
  { value: 'add_social_links', label: 'Add/update social media links', helper: 'Facebook, Instagram, YouTube, or Twitter/X page for this masjid.' },
  { value: 'duplicate_listing', label: 'Duplicate listing', helper: 'This masjid appears more than once.' },
  { value: 'closed_or_non_existing', label: 'Closed/non-existing masjid', helper: 'Listing should be reviewed for removal or archival.' },
  { value: 'inappropriate_content', label: 'Inappropriate content', helper: 'Public content is offensive, unsafe, or not relevant.' },
  { value: 'other', label: 'Other', helper: 'Any other correction request.' },
]

const initialForm = {
  reporterName: '',
  mobile: '',
  email: '',
  issueType: '' as '' | MasjidReportIssueType,
  correctionDetails: '',
  suggestedValue: '',
  // Social-media submission (issueType === 'add_social_links'). All optional; at least one required.
  facebook: '',
  instagram: '',
  youtube: '',
  twitter: '',
  proofNote: '',
  proofMedia: '',
  consent: false,
}

export default function MasjidReportPage() {
  const params = useParams<{ slug: string }>()
  const slug = params.slug
  // Pre-fill from the REAL masjid record (by slug — the same data the detail page
  // shows), never demo/placeholder. If it can't load (or the API is in degraded
  // fallback mode), fields stay empty for the user to fill — never demo values.
  const [masjid, setMasjid] = useState({ id: '', slug, nameEnglish: '', district: '', city: '', state: '', phone: '' })
  useEffect(() => {
    if (!slug) return
    let alive = true
    fetch(`/api/masjids?slug=${encodeURIComponent(slug)}&limit=1`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d || d.fallback || d.degraded) return // degraded/no DB → keep empty, never demo
        const m = Array.isArray(d.masjids) ? d.masjids[0] : null
        if (!m) return // no real record → keep empty fields
        setMasjid({
          id: String(m.id || ''),
          slug: String(m.slug || slug),
          nameEnglish: m.nameEnglish || '',
          district: m.district || '',
          city: m.city || '',
          state: m.state || '',
          phone: m.contactPhone || '',
        })
      })
      .catch(() => {})
    return () => { alive = false }
  }, [slug])
  const [form, setForm] = useState(initialForm)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [reportId, setReportId] = useState('')

  // Pre-scope from query params (e.g. the "suggest an update" CTA on the duplicate-registration
  // modal links here with ?issue=wrong_prayer_timing&suggested=…). Only accepts a valid issue type;
  // the passerby still reviews + submits — the suggestion lands as a PENDING, moderated report.
  const searchParams = useSearchParams()
  useEffect(() => {
    const issue = searchParams.get('issue') || ''
    const suggested = searchParams.get('suggested') || ''
    if (!issue && !suggested) return
    setForm((prev) => ({
      ...prev,
      issueType: issueOptions.some((o) => o.value === issue) ? (issue as MasjidReportIssueType) : prev.issueType,
      suggestedValue: suggested || prev.suggestedValue,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function setField(key: keyof typeof initialForm, value: string | boolean) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: '' }))
  }

  async function submit() {
    setSubmitting(true)
    setErrors({})

    // Social-media submission: package the four URLs into structured metadata.socialLinks (the
    // channel the admin apply engine reads on approve — it survives the JSONB round-trip intact,
    // unlike free-text). Server-side validation (https + expected host) is authoritative; this is
    // only light client-side shape checking so obvious mistakes are caught before submit.
    const isSocial = form.issueType === 'add_social_links'
    const socialLinks: Record<string, string> = {}
    if (isSocial) {
      const fieldErrors: Record<string, string> = {}
      ;([['facebook', form.facebook], ['instagram', form.instagram], ['youtube', form.youtube], ['twitter', form.twitter]] as const).forEach(([key, raw]) => {
        const value = raw.trim()
        if (!value) return
        if (!/^https?:\/\/\S+$/i.test(value)) fieldErrors[key] = 'Enter a full URL starting with https://'
        else socialLinks[key] = value
      })
      if (!Object.keys(socialLinks).length && !Object.keys(fieldErrors).length) {
        fieldErrors.social = 'Add at least one social media link.'
      }
      if (Object.keys(fieldErrors).length) {
        setErrors(fieldErrors)
        setSubmitting(false)
        return
      }
    }
    const socialSummary = Object.entries(socialLinks).map(([key, value]) => `${key}: ${value}`).join(' | ')
    const correctionDetails = isSocial
      ? (form.correctionDetails.trim()
          ? `${form.correctionDetails.trim()} — social links: ${socialSummary}`
          : `Social media links submitted for review — ${socialSummary}`)
      : form.correctionDetails
    const suggestedValue = isSocial ? socialSummary : form.suggestedValue

    try {
      const res = await fetch('/api/masjid-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masjidId: masjid.id,
          masjidSlug: masjid.slug,
          masjidName: masjid.nameEnglish,
          locality: masjid.district,
          city: masjid.city,
          reporterName: form.reporterName,
          mobile: form.mobile,
          email: form.email,
          issueType: form.issueType,
          correctionDetails,
          suggestedValue,
          metadata: isSocial ? { socialLinks } : undefined,
          proofNote: form.proofMedia
            ? [form.proofNote, `Proof media: ${form.proofMedia}`].filter(Boolean).join('\n')
            : form.proofNote,
          consent: form.consent,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setErrors(data.errors || { submit: data.error || 'Report submission failed.' })
        return
      }
      setReportId(data.report?.id || '')
      setSubmitted(true)
    } catch {
      setErrors({ submit: 'Could not reach the report service. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  function attachReportProof(asset: MediaUploadAsset) {
    const reference = asset.url || `media:${asset.id}`
    setForm((prev) => ({
      ...prev,
      proofMedia: reference,
      proofNote: prev.proofNote || `Proof file uploaded: ${asset.originalName}`,
    }))
    setErrors((prev) => ({ ...prev, proofNote: '' }))
  }

  const selectedIssue = issueOptions.find((item) => item.value === form.issueType)

  return (
    <main className="entry-page">
      <div className="entry-shell">
        <div className="entry-layout">
          <section className="entry-card">
            <Link href={`/masjids/${masjid.slug}`} className="entry-kicker"><ArrowLeft size={14} /> Back to profile</Link>
            <div>
              <h1 className="entry-title">Report a correction.</h1>
              <p className="entry-lede">
                Help keep public masjid information accurate. Reports go to a protected admin review queue before any public profile changes are made.
              </p>
            </div>

            <Card padding="lg" className="entry-card">
              <div className="entry-card-head">
                <div>
                  <span className="entry-kicker"><AlertTriangle size={14} /> Public correction</span>
                  <h2>{submitted ? 'Report submitted' : masjid.nameEnglish}</h2>
                </div>
                <StatusBadge tone={submitted ? 'emerald' : 'warning'}>{submitted ? 'Under review' : 'Review required'}</StatusBadge>
              </div>

              {submitted ? (
                <div className="entry-empty">
                  <CheckCircle2 size={42} />
                  <h2>Thank you for helping improve this profile</h2>
                  <p className="entry-muted">
                    The report is now in the admin review queue. Private reporter details are not shown publicly.
                  </p>
                  {reportId && <p className="entry-muted">Reference ID: <strong>{reportId}</strong></p>}
                  <div className="entry-actions">
                    <ButtonLink href={`/masjids/${masjid.slug}`} rightIcon={<ArrowRight size={14} />}>Return to profile</ButtonLink>
                    <ButtonLink href="/masjids" variant="secondary">Browse masjids</ButtonLink>
                  </div>
                </div>
              ) : (
                <div className="entry-form">
                  <AIAlert
                    title={`Reporting ${masjid.nameEnglish}`}
                    body={`${masjid.district}, ${masjid.city}. Corrections are reviewed before they affect prayer timings, contact details, or public trust signals.`}
                    tone="info"
                  />

                  <div className="entry-field-row">
                    <label className="entry-field">
                      <span>Reporter name</span>
                      <input value={form.reporterName} onChange={(e) => setField('reporterName', e.target.value)} placeholder="Optional" />
                    </label>
                    <label className="entry-field">
                      <span>Mobile</span>
                      <input className={errors.mobile ? 'is-invalid' : ''} value={form.mobile} onChange={(e) => setField('mobile', e.target.value)} placeholder="+91 98765 43210" />
                      {errors.mobile && <small className="entry-error">{errors.mobile}</small>}
                    </label>
                  </div>

                  <label className="entry-field">
                    <span>Email</span>
                    <input className={errors.email ? 'is-invalid' : ''} type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="Optional, for follow-up only" />
                    {errors.email && <small className="entry-error">{errors.email}</small>}
                  </label>

                  <label className="entry-field">
                    <span>Issue type *</span>
                    <select className={errors.issueType ? 'is-invalid' : ''} value={form.issueType} onChange={(e) => setField('issueType', e.target.value as MasjidReportIssueType)}>
                      <option value="">Select issue type</option>
                      {issueOptions.map((issue) => <option key={issue.value} value={issue.value}>{issue.label}</option>)}
                    </select>
                    {selectedIssue && <p className="entry-muted">{selectedIssue.helper}</p>}
                    {errors.issueType && <small className="entry-error">{errors.issueType}</small>}
                  </label>

                  {form.issueType === 'add_social_links' ? (
                    <div className="entry-field">
                      <span>Social media links</span>
                      <p className="entry-muted">Add any that apply — leave the rest blank. Links are reviewed by an admin before they appear on the public profile.</p>
                      {([
                        ['facebook', 'Facebook', 'https://facebook.com/...'],
                        ['instagram', 'Instagram', 'https://instagram.com/...'],
                        ['youtube', 'YouTube', 'https://youtube.com/@...'],
                        ['twitter', 'Twitter / X', 'https://x.com/...'],
                      ] as const).map(([key, label, placeholder]) => (
                        <label className="entry-field" key={key}>
                          <span>{label}</span>
                          <input
                            className={errors[key] ? 'is-invalid' : ''}
                            type="url"
                            inputMode="url"
                            value={form[key]}
                            onChange={(e) => setField(key, e.target.value)}
                            placeholder={placeholder}
                          />
                          {errors[key] && <small className="entry-error">{errors[key]}</small>}
                        </label>
                      ))}
                      {errors.social && <small className="entry-error">{errors.social}</small>}
                    </div>
                  ) : null}

                  <label className="entry-field">
                    <span>{form.issueType === 'add_social_links' ? 'Notes for reviewer' : 'Correction details *'}</span>
                    <textarea
                      className={errors.correctionDetails ? 'is-invalid' : ''}
                      value={form.correctionDetails}
                      onChange={(e) => setField('correctionDetails', e.target.value)}
                      placeholder={form.issueType === 'add_social_links' ? 'Optional — e.g. where you found these links (official page, masjid signboard).' : 'Describe what is wrong and where you noticed it.'}
                    />
                    {errors.correctionDetails && <small className="entry-error">{errors.correctionDetails}</small>}
                  </label>

                  {form.issueType !== 'add_social_links' ? (
                    <label className="entry-field">
                      <span>Suggested value</span>
                      <input value={form.suggestedValue} onChange={(e) => setField('suggestedValue', e.target.value)} placeholder="Correct address, timing, contact number, duplicate slug, etc." />
                    </label>
                  ) : null}

                  <AIRecommendation title="Private proof upload" body="Attach photo or document proof for admin review. Private report files are never shown on the public profile." />
                  <div className="entry-field">
                    <MediaUpload
                      label="Private proof file"
                      ownerType="report"
                      ownerId={masjid.slug}
                      masjidSlug={masjid.slug}
                      masjidId={String(masjid.id)}
                      visibility="private"
                      purpose="proof"
                      mode="any"
                      value={form.proofMedia}
                      helper="Images, PDF, Word, Excel, or text. Stored privately for review."
                      onUploaded={attachReportProof}
                      onClear={() => setField('proofMedia', '')}
                    />
                  </div>
                  <label className="entry-field">
                    <span>Proof note</span>
                    <textarea value={form.proofNote} onChange={(e) => setField('proofNote', e.target.value)} placeholder="Add photo reference, signboard observation, committee confirmation, or GPS context." />
                  </label>

                  <label className="entry-field" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
                    <input type="checkbox" checked={form.consent} onChange={(e) => setField('consent', e.target.checked)} style={{ width: 18, marginTop: 3 }} />
                    <span>I confirm this report is submitted in good faith and consent to Masjids.in contacting me if follow-up is needed.</span>
                  </label>
                  {errors.consent && <small className="entry-error">{errors.consent}</small>}
                  {errors.submit && <div className="entry-error">{errors.submit}</div>}

                  <div className="entry-actions">
                    <Button onClick={submit} disabled={submitting} rightIcon={<CheckCircle2 size={15} />}>{submitting ? 'Submitting...' : 'Submit report'}</Button>
                    <ButtonLink href={`/masjids/${masjid.slug}`} variant="outline">Cancel</ButtonLink>
                  </div>
                </div>
              )}
            </Card>
          </section>

          <aside className="entry-help-rail">
            <Card padding="md">
              <div className="entry-card-head">
                <div>
                  <Badge tone="neutral">Profile being reviewed</Badge>
                  <h3 className="card-title">{masjid.nameEnglish}</h3>
                </div>
                <ShieldCheck size={18} />
              </div>
              <div className="entry-list">
                {[
                  { icon: MapPin, title: 'Location', body: `${masjid.district}, ${masjid.city}, ${masjid.state}` },
                  { icon: Phone, title: 'Public contact', body: masjid.phone || 'Not provided' },
                  { icon: Mail, title: 'Follow-up privacy', body: 'Reporter contact stays in admin review only.' },
                  { icon: FileText, title: 'Review outcome', body: 'Admins can accept, reject, request more info, or link duplicates.' },
                ].map((item) => {
                  const Icon = item.icon
                  return (
                    <div key={item.title} className="entry-list-main">
                      <span className="entry-icon-box"><Icon size={17} /></span>
                      <div>
                        <strong>{item.title}</strong>
                        <p className="entry-muted">{item.body}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </main>
  )
}
